/**
 * What a repository *serves* — interface, API, both, or nothing to preview (§15).
 *
 * A different question from what it is written in, and the one Preview asks
 * before it decides which surfaces to offer on a project's port (§5.11). Shared
 * because the account area publishes the same field (§5.25): a workspace whose
 * projects were classified by one set of patterns and read by another would send
 * every member's app a `kind` their own app disagrees with.
 */
import type { ProjectKind, RuntimeKind } from './vocabulary';
import { listTree, type RepoReader } from './detect-read';

/**
 * Three places a repository says what it answers with, in the order they are
 * trusted. A dependency list is the clearest, but half the ecosystems do not
 * settle the question there — `spring-boot-starter-web` serves pages and JSON
 * alike, a Go service imports nothing but the standard library — so the tree
 * and the entry points are read too, and one strong signal from any of them
 * decides. Every field arrives lowercased, which is what lets the patterns
 * below be lowercase-only.
 */
export interface KindSignals {
  runtime: RuntimeKind;
  /** Every manifest the repository has, concatenated. */
  manifests: string;
  /**
   * Its tree a few levels down, one repository-relative path per line, with a
   * trailing `/` on directories so an empty `templates/` still says its name.
   */
  paths: string;
  /** The head of the entry points worth reading, concatenated. */
  sources: string;
  /** Whether anything here is started at all — a port or a run command. */
  serves: boolean;
}

/** Any folder prefix — the layouts differ, the file name does not. */
const ANYWHERE = String.raw`(?:[\w.@-]+\/)*`;

/**
 * Dependencies that mean "this repository renders an interface". Matched as
 * substrings rather than as parsed names: the manifests are nine different
 * formats, and the question — is a view layer in here at all — is coarse
 * enough that reading them properly would buy nothing. The plain words are
 * bounded, because unbounded they match the middle of an unrelated one
 * (`tera` inside `iteration`) and a single false hit decides the answer.
 */
const UI_DEPS =
  /@remix-run\/|@angular\/core|@sveltejs\/|@docusaurus|@11ty\/|@stencil\/|@vue\/|react-dom|react-scripts|solid-js|ember-source|laravel\/framework|symfony\/framework-bundle|jakarta\.faces|spring-boot-starter-(?:thymeleaf|mustache|freemarker|groovy-templates)|\b(?:next|nuxt|astro|svelte|vue|preact|gatsby|qwik|blitz|redwood|vitepress|vuepress|gridsome|htmx|alpinejs|ejs|pug|handlebars|nunjucks|mustache|liquidjs|marko|streamlit|gradio|nicegui|reflex|jinja2|thymeleaf|freemarker|vaadin|wicket|jte|templ|hugo|askama|tera|maud|yew|leptos|dioxus|sycamore|twig|livewire|inertiajs|filament|rails|jekyll|middleman|phoenix|flutter)\b/;

/** Dependencies that mean "this repository answers calls, and only calls". */
const API_DEPS =
  /@nestjs\/core|@hapi\/hapi|@apollo\/server|@trpc\/server|@fastify\/|@adonisjs\/|@feathersjs\/|graphql-yoga|itty-router|gin-gonic|labstack\/echo|gofiber|go-chi|gorilla\/mux|gqlgen|danielgtaylor\/huma|djangorestframework|django-ninja|flask-restful|flask-restx|flask-smorest|strawberry-graphql|actix-web|spring-boot-starter-webflux|jakarta\.ws\.rs|micronaut-http|quarkus-rest|api-platform|slim\/slim|rails-api|swashbuckle|microsoft\.aspnetcore\.openapi|\b(?:express|fastify|koa|restify|hono|elysia|polka|loopback|moleculer|fastapi|litestar|starlette|sanic|tornado|falcon|aiohttp|bottle|pyramid|connexion|graphene|springdoc|swagger|openapi|jersey|resteasy|axum|rocket|warp|tonic|poem|salvo|utoipa|grape|lumen|grpc)\b/;

/** A page to open, a template to render, or a component that becomes one. */
const UI_PATHS = new RegExp(
  `^${ANYWHERE}index\\.html$|^${ANYWHERE}(?:templates|views|pages|layouts)\\/$|\\.(?:cshtml|razor|erb|haml|blade\\.php|hbs|ejs|pug|njk|jsx|tsx|vue|svelte)$`,
  'm'
);

/**
 * A written contract, or the routes a framework keeps apart from its pages —
 * `app/api/`, `server/api/`, `+server.ts`, `routes/api.php`. Those are how a
 * full-stack repository says it is *also* an API, and they are the reason
 * `both` exists: nothing in the manifest of a Next.js app mentions the route
 * handlers sitting beside its pages.
 */
const API_PATHS = new RegExp(
  `^${ANYWHERE}(?:openapi|swagger)\\.(?:ya?ml|json)$|\\.proto$|\\+server\\.[jt]s$|^${ANYWHERE}(?:app|pages|server|src)\\/api\\/$|^${ANYWHERE}routes\\/api\\.php$`,
  'm'
);

/**
 * What the entry points do with a request. `@controller` does not match
 * `@restcontroller` — the annotation is the whole difference between a Spring
 * project that returns a page and one that returns JSON, and the manifest of
 * both says `spring-boot-starter-web`.
 */
const UI_CODE =
  /render_template\(|res\.render\(|->render\(|addrazorpages|addcontrollerswithviews|html\/template|@controller\b|createinertiaapp/;

const API_CODE =
  /@restcontroller|@getmapping|@postmapping|@path\(|mapcontrollers\(|addswaggergen|addcontrollers\(|res\.json\(|jsonify\(|http\.handlefunc|@app\.(?:get|post|put|delete)\(|@router\./;

/**
 * Evidence that proves nothing on its own — a CSS file, an ORM, a folder named
 * after a layer — and settles it when nothing stronger is there. Each match
 * counts once for its side; the sides are then compared, so a repository with
 * three faint reasons to be an API and one to be an interface is an API.
 */
const HINTS: { kind: 'ui' | 'api'; on: 'manifests' | 'paths' | 'sources'; test: RegExp }[] = [
  {
    kind: 'ui',
    on: 'manifests',
    test: /tailwindcss|@mui\/|chakra-ui|styled-components|emotion|\b(?:bootstrap|antd|sass|less|postcss|vite|webpack|parcel|rollup|django|flask|sinatra|laravel|symfony)\b/
  },
  {
    kind: 'ui',
    on: 'paths',
    test: new RegExp(
      `\\.(?:css|scss|sass|less)$|^${ANYWHERE}(?:components|public|static|assets|www|frontend|client)\\/$`,
      'm'
    )
  },
  { kind: 'ui', on: 'sources', test: /text\/html|content-type["'\s:]+text|staticfiles|usestaticfiles/ },
  {
    kind: 'api',
    on: 'manifests',
    test: /drizzle-orm|class-validator|spring-boot-starter-(?:web|data|security)|\b(?:uvicorn|gunicorn|hypercorn|daphne|pydantic|sqlalchemy|alembic|mongoose|prisma|typeorm|sequelize|knex|jsonwebtoken|passport|helmet|cors|celery|serde_json|jackson)\b/
  },
  {
    kind: 'api',
    on: 'paths',
    test: new RegExp(
      `^${ANYWHERE}(?:controllers|handlers|endpoints|resolvers|serializers|routers?|migrations|entities|repositories)\\/$|\\.(?:http|rest)$`,
      'm'
    )
  },
  {
    kind: 'api',
    on: 'sources',
    test: /application\/json|json\.marshal|marshaljson|@app\.route|blueprint\(|listenandserve/
  }
];

/**
 * Interface, API, or both.
 *
 * A view layer and a request layer coexist constantly: a Next.js app has route
 * handlers, a Django site has DRF beside its templates. When both state their
 * case the answer is `both`, and the project appears on both surfaces — the
 * page is not hidden behind the console, and the console is not hidden behind
 * the page. Below that, the faint signals are weighed against each other; a tie
 * is `ui`, because a repository that only *hints* at a request layer is not
 * worth taking the browser away from. Only then does the shape of the
 * repository answer: something that is *started* and has shown not one sign of
 * a view layer is a service, and `unknown` is kept for a repository that serves
 * nothing at all — a library, a CLI, a monorepo root — where the honest answer
 * is that there is nothing to preview.
 */
export function classifyKind(signals: KindSignals): ProjectKind {
  const ui = UI_DEPS.test(signals.manifests) || UI_PATHS.test(signals.paths) || UI_CODE.test(signals.sources);
  const api = API_DEPS.test(signals.manifests) || API_PATHS.test(signals.paths) || API_CODE.test(signals.sources);
  if (ui && api) return 'both';
  if (ui) return 'ui';
  if (api) return 'api';
  if (signals.runtime === 'static') return 'ui';
  const faintUi = weigh('ui', signals);
  const faintApi = weigh('api', signals);
  if (faintUi || faintApi) return faintUi >= faintApi ? 'ui' : 'api';
  return signals.serves ? 'api' : 'unknown';
}

function weigh(kind: 'ui' | 'api', signals: KindSignals): number {
  return HINTS.filter((hint) => hint.kind === kind && hint.test.test(signals[hint.on])).length;
}

/** Manifests worth reading for the question above, whatever the runtime is. */
const MANIFESTS = [
  'package.json',
  'deno.json',
  'pyproject.toml',
  'requirements.txt',
  'setup.py',
  'Pipfile',
  'pom.xml',
  'build.gradle',
  'build.gradle.kts',
  'build.sbt',
  'go.mod',
  'Cargo.toml',
  'composer.json',
  'Gemfile',
  'mix.exs',
  'pubspec.yaml'
];

/** How far the tree is listed, and how much of it is kept. */
const TREE_DEPTH = 4;
const TREE_CAP = 800;

export async function detectKind(
  reader: RepoReader,
  runtime: RuntimeKind,
  files: Set<string>,
  serves: boolean
): Promise<ProjectKind> {
  const tree = await listTree(reader, '', TREE_DEPTH, TREE_CAP);
  return classifyKind({
    runtime,
    manifests: (await readManifests(reader, files)).toLowerCase(),
    paths: tree.join('\n').toLowerCase(),
    sources: (await readEntryPoints(reader, tree)).toLowerCase(),
    serves
  });
}

async function readManifests(reader: RepoReader, files: Set<string>): Promise<string> {
  // A .NET project names its manifest after the assembly, not after the build
  // system, so it is the one that has to be found rather than listed.
  const named = [
    ...MANIFESTS.filter((file) => files.has(file)),
    ...[...files].filter((file) => /\.(?:csproj|fsproj|vbproj)$/i.test(file))
  ];
  const texts = await Promise.all(named.map((file) => reader.read(file)));
  return texts.filter(Boolean).join('\n');
}

/**
 * Files whose first lines say what the repository answers with, picked out of
 * the tree so one pattern covers every layout the ecosystems use.
 */
const ENTRY_FILES =
  /^(?:(?:src|app|api|routes|config|public|cmd\/[\w.-]+)\/)?(?:index|main|app|server|api|wsgi|asgi|program|startup|routes|web)\.(?:m?[jt]sx?|py|go|rs|cs|php|rb)$/i;

/**
 * A JVM project keeps its classes far deeper than the tree is listed, and the
 * annotation in them is the whole answer — so those roots are walked on their
 * own, for the few files the conventions name.
 */
const JVM_SOURCE_ROOTS = ['src/main/java', 'src/main/kotlin'];
const JVM_ENTRY = /(?:controller|resource|router|application|api)\.(?:java|kt)$/i;

/** Enough of a file to hold its imports and its first route. */
const SOURCE_HEAD = 8000;
const SOURCE_FILES = 12;

async function readEntryPoints(reader: RepoReader, tree: string[]): Promise<string> {
  const picked = tree.filter((path) => ENTRY_FILES.test(path)).slice(0, 8);
  for (const root of JVM_SOURCE_ROOTS) {
    if (!tree.includes(`${root}/`)) continue;
    const deep = await listTree(reader, root, 8, 400);
    picked.push(
      ...deep
        .filter((path) => JVM_ENTRY.test(path))
        .slice(0, 4)
        .map((path) => `${root}/${path}`)
    );
  }
  const texts = await Promise.all(
    picked.slice(0, SOURCE_FILES).map(async (rel) => (await reader.read(rel))?.slice(0, SOURCE_HEAD) ?? '')
  );
  return texts.join('\n');
}
